import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { hoyISO } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MotivoAusenciaJustificadaFields from "@/components/MotivoAusenciaJustificadaFields";

const TIPOS_BASE = [
  { id: "vacaciones", label: "Vacaciones" },
  { id: "baja", label: "Baja" },
  { id: "asuntos_propios", label: "Asuntos propios" },
  { id: "ausencia_justificada", label: "Ausencia justificada" },
];

export default function RegistrarAusenciaDialog({
  open,
  onOpenChange,
  usuarios = [],
  usuarioId = "",
  fechaReferencia = null,
  asuntosPropiosActivo = false,
  permisosAprobacion = null,
  onSuccess,
}) {
  const { user: usuarioActual } = useAuth();
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState({
    usuario_id: "",
    tipo: "vacaciones",
    fecha_inicio: hoyISO(),
    fecha_fin: hoyISO(),
    motivo: "",
    comentario: "",
  });

  const tipos = useMemo(
    () =>
      TIPOS_BASE.filter((t) => {
        if (t.id === "asuntos_propios" && !asuntosPropiosActivo) return false;
        if (permisosAprobacion && permisosAprobacion[t.id] === false) return false;
        return true;
      }),
    [asuntosPropiosActivo, permisosAprobacion],
  );

  const empleadosElegibles = useMemo(
    () => usuarios.filter((u) => u.activo && ["worker", "manager", "admin"].includes(u.rol)),
    [usuarios],
  );

  useEffect(() => {
    if (!open) return;
    const f = fechaReferencia || hoyISO();
    const tipoInicial = tipos[0]?.id || "vacaciones";
    setForm({
      usuario_id: usuarioId ? String(usuarioId) : "",
      tipo: tipoInicial,
      fecha_inicio: f,
      fecha_fin: f,
      motivo: "",
      comentario: "",
    });
  }, [open, usuarioId, fechaReferencia, tipos]);

  useEffect(() => {
    if (tipos.length && !tipos.some((t) => t.id === form.tipo)) {
      setForm((prev) => ({ ...prev, tipo: tipos[0].id }));
    }
  }, [tipos, form.tipo]);

  const guardar = async () => {
    if (!form.usuario_id) {
      toast.error("Selecciona un empleado");
      return;
    }
    if (!form.motivo.trim() || form.motivo.trim().length < 3) {
      toast.error("Indica el motivo (mínimo 3 caracteres)");
      return;
    }
    setGuardando(true);
    try {
      await api.post("/solicitudes/por-trabajador", {
        usuario_id: Number(form.usuario_id),
        tipo: form.tipo,
        fecha_inicio: form.fecha_inicio,
        fecha_fin: form.fecha_fin,
        motivo: form.motivo.trim(),
        comentario: form.comentario.trim(),
      });
      toast.success(
        Number(form.usuario_id) === usuarioActual?.id
          ? "Ausencia registrada correctamente."
          : "Ausencia registrada. El trabajador recibirá aviso en la app y por email si está configurado.",
      );
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="registrar-ausencia-dialog">
        <DialogHeader>
          <DialogTitle>Registrar ausencia</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-zinc-500">
          Para solicitudes presenciales: vacaciones, baja
          {asuntosPropiosActivo ? ", asuntos propios" : ""}
          {tipos.some((t) => t.id === "ausencia_justificada") ? " o ausencia justificada" : ""}.
          Queda aprobada y el trabajador es notificado.
          {tipos.some((t) => t.id === "ausencia_justificada") && " La ausencia justificada imputa la jornada como horas trabajadas."}
        </p>
        {tipos.length === 0 ? (
          <p className="text-sm text-amber-700">No tienes permiso para registrar ningún tipo de ausencia.</p>
        ) : (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Empleado</Label>
            <Select value={form.usuario_id} onValueChange={(v) => setForm({ ...form, usuario_id: v })}>
              <SelectTrigger data-testid="ausencia-usuario-select">
                <SelectValue placeholder="Selecciona empleado" />
              </SelectTrigger>
              <SelectContent>
                {empleadosElegibles.map((u) => (
                  <SelectItem key={u.id} value={String(u.id)}>
                    {u.nombre} {u.apellidos}
                    {usuarioActual?.id === u.id ? " (yo)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Tipo</Label>
            <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
              <SelectTrigger data-testid="ausencia-tipo-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {tipos.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label>Desde</Label>
              <Input type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Hasta</Label>
              <Input type="date" value={form.fecha_fin} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            {form.tipo === "ausencia_justificada" ? (
              <MotivoAusenciaJustificadaFields
                value={form.motivo}
                onChange={(v) => setForm({ ...form, motivo: v })}
              />
            ) : (
              <>
                <Label>Motivo</Label>
                <Textarea value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} placeholder="Motivo indicado por el trabajador" data-testid="ausencia-motivo-input" />
              </>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Comentario interno (opcional)</Label>
            <Textarea value={form.comentario} onChange={(e) => setForm({ ...form, comentario: e.target.value })} placeholder="Notas para el historial" />
          </div>
        </div>
        )}
        <DialogFooter>
          <Button onClick={guardar} disabled={guardando || tipos.length === 0} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ausencia-guardar-btn">
            {guardando ? "Guardando…" : "Registrar y notificar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
